import { NavLink, Link, useNavigate } from 'react-router-dom'
import { Home, CheckSquare, Clock, CalendarDays, Settings, LogOut, Pencil, Mic, Camera, Bot, Star, Lock, Shield } from 'lucide-react'
import useAuthStore from '../store/authStore'
import useTaskStore from '../store/taskStore'
import { useFeature, planDisplayName } from '../config/features'
import { useT } from '../i18n/useT'

export default function Sidebar() {
  const t   = useT()
  const can = useFeature()
  const { user, logout } = useAuthStore()
  const { openQuickAdd, openPremiumModal, openAgent } = useTaskStore()
  const navigate  = useNavigate()
  const userPlan  = user?.plan || 'free'
  const isPaid    = userPlan === 'pro' || userPlan === 'premium'

  const NAV = [
    { to: '/',           icon: Home,         key: 'nav_dashboard' },
    { to: '/attivita',   icon: CheckSquare,  key: 'nav_tasks'     },
    { to: '/calendario', icon: CalendarDays, key: 'nav_calendar'  },
    { to: '/scadenze',   icon: Clock,        key: 'nav_deadlines' },
    { to: '/profilo',    icon: Settings,     key: 'nav_profile'   },
  ]

  const ADD_OPTIONS = [
    { mode: 'manual', icon: Pencil, labelKey: 'fab_manual', feature: null          },
    { mode: 'voice',  icon: Mic,    labelKey: 'fab_voice',  feature: 'voice_input' },
    { mode: 'image',  icon: Camera, labelKey: 'fab_image',  feature: 'image_input' },
  ]

  const handleLogout = async () => { await logout(); navigate('/login') }

  const handleAgent = () => {
    if (can('agent_chat')) openAgent()
    else openPremiumModal('premium')
  }

  return (
    <aside className="sidebar animate-fadeIn">
      <div className="sb-brand">
        <div className="sb-brand-icon"><img src="/icon-512.png" alt="Mindesk" className="dash-brand-logo" /></div>
        <span className="sb-brand-text">Mindesk</span>
      </div>

      <nav className="sb-nav" id="tour-sidebar-nav">
        {NAV.map(({ to, icon: Icon, key }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => `sb-nav-item${isActive ? ' active' : ''}`}
          >
            <Icon size={17} strokeWidth={2} />
            <span>{t(key)}</span>
          </NavLink>
        ))}
      </nav>

      {/* Agent button */}
      <div className="sb-agent-wrap" id="tour-sidebar-agent">
        <button
          className={`sb-agent-btn${!can('agent_chat') ? ' sb-agent-btn--locked' : ''}`}
          onClick={handleAgent}
        >
          <Bot size={17} strokeWidth={2} />
          <span style={{ flex: 1 }}>Mindesk Agent</span>
          {!can('agent_chat') && <Lock size={12} strokeWidth={2.5} style={{ opacity: 0.6 }} />}
        </button>
      </div>

      <div className="sb-add-wrap" id="tour-sidebar-add">
        {ADD_OPTIONS.map(({ mode, icon: Icon, labelKey, feature }, i) => {
          const locked = feature ? !can(feature) : false
          return (
            <button
              key={mode}
              className={`sb-add-btn${i > 0 ? ' sb-add-btn--secondary' : ''}${locked ? ' sb-add-btn--locked' : ''}`}
              onClick={() => locked ? openPremiumModal() : openQuickAdd(mode)}
              title={locked ? t('sb_feature_locked') : undefined}
            >
              <Icon size={16} strokeWidth={2.5} />
              <span style={{ flex: 1 }}>{t(labelKey)}</span>
              {locked && <Lock size={12} strokeWidth={2.5} style={{ opacity: 0.6 }} />}
            </button>
          )
        })}
      </div>

      <div style={{ flex: 1 }} />

      {isPaid ? (
        <div className="sb-premium-wrap">
          <div className="sb-plan-badge">
            <Star size={13} strokeWidth={2.5} />
            {t('sb_plan_label', { plan: planDisplayName(userPlan) })}
          </div>
        </div>
      ) : (
        <div className="sb-premium-wrap">
          <button className="sb-premium-btn" onClick={openPremiumModal}>
            <Star size={15} strokeWidth={2.5} />
            {t('sb_premium')}
          </button>
        </div>
      )}

      <div style={{ textAlign: 'center', padding: '0 12px 8px' }}>
        <Link to="/privacy" style={{ fontSize: '0.72rem', color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
          <Shield size={11} /> Privacy Policy
        </Link>
      </div>

      <div className="sb-user">
        <div className="sb-user-avatar">{user?.avatar || '?'}</div>
        <div className="sb-user-info">
          <div className="sb-user-name">{user?.name}</div>
          {user?.familyName && (
            <div className="sb-user-sub">Famiglia {user.familyName}</div>
          )}
        </div>
        <button className="sb-logout-btn" onClick={handleLogout} title={t('profile_logout')}>
          <LogOut size={15} />
        </button>
      </div>
    </aside>
  )
}
